import { useEffect, useId, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import type { TipDokumenta } from "@/lib/dokumenti";

/** Shared searchable catalogue picker; the stored value always remains a code. */
export function DocumentTypeCombobox({ id, value, onChange, types, disabled = false, required = false }: {
  id?: string; value: string; onChange: (code: string) => void; types: TipDokumenta[]; disabled?: boolean; required?: boolean;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-options`;
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const selected = types.find((type) => type.code === value);
  const options = types.filter((type) => (type.active_version?.name_hr ?? type.code).toLocaleLowerCase("hr").includes(query.toLocaleLowerCase("hr")));
  const index = Math.min(active, Math.max(0, options.length - 1));
  useEffect(() => {
    const list = listRef.current;
    const option = list?.children[index] as HTMLElement | undefined;
    if (!list || !option) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }, [index, open, query]);
  function choose(code: string) { onChange(code); setOpen(false); setQuery(""); }
  function show() { setOpen(true); setQuery(""); setActive(0); }
  return <div className="relative min-w-0">
    <Input id={inputId} role="combobox" aria-label="Tip dokumenta" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} aria-activedescendant={open && options[index] ? `${listId}-${index}` : undefined}
      required={required} disabled={disabled} autoComplete="off" placeholder="Pretražite tip dokumenta"
      value={open ? query : selected?.active_version?.name_hr ?? value}
      onFocus={show} onClick={() => { if (!open) show(); }}
      onBlur={() => { setOpen(false); setQuery(""); }}
      onChange={(event) => { setQuery(event.target.value); setOpen(true); setActive(0); }}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault(); event.stopPropagation();
          if (!open) show(); else setActive(Math.max(0, Math.min(options.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
        } else if (event.key === "Enter" && open) {
          event.preventDefault(); event.stopPropagation(); if (options[index]) choose(options[index].code);
        } else if (event.key === "Escape" && open) {
          event.preventDefault(); event.stopPropagation(); setOpen(false); setQuery("");
        }
      }} />
    {open && <ul ref={listRef} id={listId} role="listbox" aria-label="Tipovi dokumenata" className="absolute z-40 mt-1 max-h-60 w-full overflow-y-auto rounded border border-slate-400 bg-white p-1 shadow-lg">
      {options.map((type, position) => <li id={`${listId}-${position}`} key={type.id} role="option" aria-selected={position === index} className={`cursor-pointer rounded px-2 py-2 text-sm ${position === index ? "bg-blue-100 text-blue-950" : "hover:bg-slate-100"}`} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(type.code)}>{type.active_version?.name_hr ?? type.code}</li>)}
      {!options.length && <li className="px-2 py-2 text-sm text-muted-foreground" role="status">Nema pronađenih tipova.</li>}
    </ul>}
  </div>;
}
